import { useContext } from "react";
import { Box, Link, Typography } from "@mui/material";
import { sxBody, sxHeading, sxSection } from "../CandidateAccordions";
import { CandidateContext } from "../../../CandidateProfile";
import {
  AddNewEmailAddress,
  AddNewPhoneNumber,
  DisplayAndEditEmailAddress,
  DisplayAndEditPhoneNumber,
} from "./EditProfileComponents";
import { AppFeatureLookup } from "../../../../App/Constants/AbacConstants";
import { CandidatePhoneNumberType, CandidateEmailType } from "../../../../App/Enums/CandidateEnums";
import { useFeaturePermission } from "../../../../App/State/Hooks/abacHooks";
import { ICandidatePhoneNumber, ICandidateEmailAddress } from "../../../../App/Typings/Candidate";
import { useFlags } from "launchdarkly-react-client-sdk";
import { IFeatureToggles } from "../../../../App/Typings/FeatureToggles";
import {
  DisplayEmailAddress,
  DisplayPhoneNumber,
} from "../../../../ManageCandidates/Components/CandidateRow/CandidateRow";

export function ContactInformation() {
  const {
    candidate: { phoneNumbers, emailAddresses },
  } = useContext(CandidateContext);

  const { enableCandidateProfileEditing } = useFlags<IFeatureToggles>();

  return (
    <Box>
      <Box sx={sxSection}>
        <Typography data-test={"candidate-header-phone"} sx={sxHeading}>
          Phone:
        </Typography>
        <Box
          data-test={"candidate-phone"}
          sx={{ ...sxBody, display: "flex", flexDirection: "column", gap: "0.25em" }}
        >
          {enableCandidateProfileEditing ? (
            <>
              {phoneNumbers.map((phone, idx) => (
                <DisplayAndEditPhoneNumber key={idx} candidatePhoneNumber={phone} />
              ))}
              <AddNewPhoneNumber />
            </>
          ) : (
            <PhoneNumbers phoneNumbers={phoneNumbers} />
          )}
        </Box>
      </Box>

      <Box sx={sxSection}>
        <Typography data-test={"candidate-header-email"} sx={sxHeading}>
          Email:
        </Typography>
        <Box
          data-test={"candidate-email"}
          sx={{ ...sxBody, display: "flex", flexDirection: "column", gap: "0.25em" }}
        >
          {enableCandidateProfileEditing ? (
            <>
              {emailAddresses.map((email, idx) => (
                <DisplayAndEditEmailAddress key={idx} candidateEmailAddress={email} />
              ))}
              <AddNewEmailAddress />
            </>
          ) : (
            <EmailAddresses emailAddresses={emailAddresses} />
          )}
        </Box>
      </Box>
    </Box>
  );
}

const PhoneNumbers = ({ phoneNumbers }: { phoneNumbers: ICandidatePhoneNumber[] }) => {
  const callCandidateAccess = useFeaturePermission(AppFeatureLookup.ProfileCallCandidateAction);
  return (
    <>
      {phoneNumbers &&
        phoneNumbers.map((number, idx) => {
          const { phoneNumber } = number;
          return callCandidateAccess && number.isValid ? (
            <Link
              key={`candidate-email-${idx}`}
              href={`tel:${phoneNumber}`}
              title={`Call ${phoneNumber}`}
              sx={{
                textDecoration: "none",
              }}
            >
              <DisplayPhoneNumber
                phone={number}
                suffix={<> ({CandidatePhoneNumberType[number.phoneType]})</>}
              />
            </Link>
          ) : (
            <Typography component="span" key={`candidate-phone-${idx}`} sx={{ fontWeight: "normal" }}>
              <DisplayPhoneNumber
                phone={number}
                suffix={<> ({CandidatePhoneNumberType[number.phoneType]})</>}
              />
            </Typography>
          );
        })}
    </>
  );
};

const EmailAddresses = ({ emailAddresses }: { emailAddresses: ICandidateEmailAddress[] }) => {
  return (
    <>
      {emailAddresses &&
        emailAddresses.map((email, idx) => (
          <Typography key={`candidate-email-${idx}`} sx={{ fontWeight: "normal" }}>
            <DisplayEmailAddress
              email={email}
              suffix={<>(${CandidateEmailType[email.emailAddressType]})</>}
            />
          </Typography>
        ))}
    </>
  );
};
